import { REQUEST_TYPES } from '../requestTypes';
import { GROUP_BY, USER_ROLES } from '../../../utils/constants';
import { ENTITY_NAMES } from '../entityNames';
import { ENDPOINTS } from '../../../config/config';
import { API_URLS } from '../../constants';
import { isAgent } from '../../../utils/user';
import { type RequestHandlerFactory } from '../../types';

const filtersMap: Record<string, any> = {
    [USER_ROLES.COMPANY_EXTERNAL]: {
        agencies: () => ({ groupBy: GROUP_BY.ALL, type: 'agency', filter: [] }),
        locations: () => ({ groupBy: GROUP_BY.ALL, type: 'location', filter: [] }),
        agents: () => ({
            groupBy: GROUP_BY.ALL,
            type: 'agent',
            filter: []
        })
    },
    [USER_ROLES.COMPANY]: {
        agencies: () => ({ groupBy: GROUP_BY.ALL, type: 'agency', filter: [] }),
        locations: () => ({ groupBy: GROUP_BY.ALL, type: 'location', filter: [] }),
        agents: () => ({
            groupBy: GROUP_BY.ALL,
            type: 'agent',
            filter: []
        })
    },
    [USER_ROLES.AGENCY]: {
        agencies: (user: any) => ({
            groupBy: GROUP_BY.AGENCY,
            type: 'agency',
            filter: [user.agencyId]
        }),
        locations: (user: any) => ({
            groupBy: GROUP_BY.AGENCY,
            type: 'location',
            filter: [user.agencyId]
        }),
        agents: (locations: any[]) => ({
            groupBy: GROUP_BY.LOCATION,
            type: 'agent',
            filter: locations.map(item => item.id)
        })
    },
    [USER_ROLES.LOCATION]: {
        locations: (user: any) => ({
            groupBy: GROUP_BY.LOCATION,
            type: 'location',
            filter: [user.locationId]
        }),
        agents: (locations: any, user: any) => ({
            groupBy: GROUP_BY.LOCATION,
            type: 'agent',
            filter: [user.locationId]
        })
    },
    [USER_ROLES.CORE_TEAM_LEAD]: {
        locations: (user: any) => ({
            groupBy: GROUP_BY.LOCATION,
            type: 'location',
            filter: [user.locationId]
        }),
        agents: (_: any, user: any, teamIds: any[]) => ({
            groupBy: GROUP_BY.TEAM,
            type: 'agent',
            filter: teamIds
        })
    },
    [USER_ROLES.CORE_AGENT]: {}
};

const getFilters = (user: any) => {
    const filters = filtersMap[user.role];
    if (!filters) {
        throw new Error(`Unsupported role: ${user.role || ''}`);
    }

    return filters;
};

export const commonRequests: Record<string, RequestHandlerFactory> = {
    [REQUEST_TYPES.GET_CONFIG]: () => {
        const descriptors = {
            [ENTITY_NAMES.DASHBOARD_SETTINGS]: {
                method: 'get',
                url: `/v1/users/me/config/dashboard-settings`,
                defaultEndpoint: ENDPOINTS.MANAGEMENT_CONSOLE_URL
            }
        };

        return {
            transformResponse: response => response[ENTITY_NAMES.DASHBOARD_SETTINGS]?.data,
            descriptors
        };
    },
    [REQUEST_TYPES.GET_COMMON_DATA]: params => {
        const filters = getFilters(params.user);
        const descriptors: any = {
            locations: {
                method: 'get',
                url: API_URLS.ORGANIZATION_LOCATIONS,
                defaultEndpoint: ENDPOINTS.MANAGEMENT_CONSOLE_URL
            }
        };

        if (filters.agencies) {
            Object.assign(descriptors, {
                agencies: {
                    method: 'get',
                    url: API_URLS.ORGANIZATION_AGENCIES,
                    defaultEndpoint: ENDPOINTS.MANAGEMENT_CONSOLE_URL
                }
            });
        }

        return {
            transformResponse: res => {
                const { agencies, locations } = res;

                const entities: any = {
                    [ENTITY_NAMES.LOCATIONS]: locations.data
                };

                if (filters.agencies) {
                    Object.assign(entities, {
                        [ENTITY_NAMES.AGENCIES]: agencies.data
                    });
                }

                return { entities };
            },
            descriptors
        };
    },
    [REQUEST_TYPES.GET_TEAMS_LIST]: () => {
        return {
            transformResponse: res => {
                const agents: any[] = [];

                for (const team of res.teams.data) {
                    if (Array.isArray(team.members)) {
                        team.members.forEach((member: any) => {
                            if (isAgent(member.role)) {
                                agents.push(member);
                            }
                        });
                    }
                }

                return {
                    entities: {
                        [ENTITY_NAMES.TEAMS]: res.teams.data,
                        [ENTITY_NAMES.AGENTS]: agents
                    }
                };
            },
            descriptors: {
                teams: {
                    method: 'get',
                    url: API_URLS.ORGANIZATION_TEAMS,
                    defaultEndpoint: ENDPOINTS.MANAGEMENT_CONSOLE_URL,
                    params: {
                        agent_active_only: true
                    }
                }
            }
        };
    }
};
